// frontend/src/hooks/useConsent.ts
//
// PRIVACY-CONSENT-01 — React hook for cookie consent state.
// Quebec Loi 25 + GDPR Art. 7 + ePrivacy Directive compliant.
//
// Design:
//   - State is persisted in localStorage under `xprojex_consent_state` as JSON.
//   - Every decision (accept / reject / granular / withdraw) is POSTed to
//     `/api/consent` so we have a server-side audit log (required by Loi 25).
//   - The hook works for both authenticated and anonymous users; the backend
//     endpoint accepts anonymous consent via session_id + ip_hash.
//   - Backwards-compatible with the legacy boolean key `xprojex_tracking_consent`
//     previously used by marketingAttribution.ts (if present and `true`, users
//     are grandfathered into analytics-enabled consent until they revise).
//   - The non-React utility `hasConsentFor(category)` is exported for callers
//     that aren't inside React (e.g. marketingAttribution.ts service module).

import { useCallback, useEffect, useState } from 'react';
import logger from '../utils/logger';
import { API_CONFIG } from '../utils/config';

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';

export interface ConsentCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState {
  version: number;
  categories: ConsentCategories;
  acceptedAt: string; // ISO timestamp
  withdrawnAt: string | null;
  sessionId: string; // client-generated, persisted across tabs
}

export const CONSENT_STORAGE_KEY = 'xprojex_consent_state';
export const LEGACY_TRACKING_CONSENT_KEY = 'xprojex_tracking_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_CHANGE_EVENT = 'xprojex:consent-changed';

const NECESSARY_ONLY: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const ALL_GRANTED: ConsentCategories = {
  necessary: true,
  analytics: true,
  marketing: true,
};

/**
 * Read the persisted consent state from localStorage. Returns null if the user
 * has not yet made a decision. Gracefully handles legacy boolean key as an
 * implicit "analytics accepted" decision (so we don't nag grandfathered users).
 */
export function readConsentState(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ConsentState;
      if (parsed && parsed.categories && typeof parsed.categories.necessary === 'boolean') {
        return parsed;
      }
    }

    // Legacy migration — if the user previously accepted tracking via the
    // boolean key, treat that as analytics-granted consent. Do NOT write
    // the new shape yet; let the next explicit decision do that so users
    // still see the banner one more time to confirm granular choices.
    const legacy = localStorage.getItem(LEGACY_TRACKING_CONSENT_KEY);
    if (legacy === 'true') {
      return {
        version: CONSENT_VERSION,
        categories: { ...ALL_GRANTED, marketing: false }, // conservative: no marketing via legacy
        acceptedAt: new Date(0).toISOString(), // epoch to signal legacy grandfather
        withdrawnAt: null,
        sessionId: getOrCreateSessionId(),
      };
    }
  } catch (err) {
    logger.warn('[useConsent] Failed to read consent state:', err);
  }
  return null;
}

/**
 * Non-React utility — check if a specific category is currently granted.
 * Use this from services/modules that cannot call React hooks.
 *
 * Returns `false` if the user has not yet decided OR has withdrawn.
 * `necessary` is always true (site can't function without it).
 */
export function hasConsentFor(category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  const state = readConsentState();
  if (!state) return false;
  if (state.withdrawnAt) return false;
  return !!state.categories[category];
}

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem('xprojex_consent_session_id');
    if (existing) return existing;
    const fresh = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    sessionStorage.setItem('xprojex_consent_session_id', fresh);
    return fresh;
  } catch {
    return `${Date.now()}-fallback`;
  }
}

async function postConsentToBackend(
  path: '/api/consent' | '/api/consent/withdraw',
  body: Record<string, unknown>
): Promise<void> {
  try {
    const res = await fetch(`${API_CONFIG.BACKEND_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      logger.warn('[useConsent] Backend consent POST not OK:', res.status);
    }
  } catch (err) {
    // Non-fatal: we've already saved to localStorage; the server audit log
    // will be slightly behind but the UX is uninterrupted.
    logger.warn('[useConsent] Failed to POST consent to backend:', err);
  }
}

export interface UseConsentReturn {
  /** Current consent state; `null` if user hasn't decided yet. */
  state: ConsentState | null;
  /** True if the user has made ANY decision (ready to hide the banner). */
  isDecided: boolean;
  /** Category-specific check (wraps `hasConsentFor`). */
  hasConsent: (category: ConsentCategory) => boolean;
  /** Accept all non-necessary categories. */
  acceptAll: () => Promise<void>;
  /** Reject all non-necessary categories (necessary stays true). */
  rejectAll: () => Promise<void>;
  /** Save a custom combination of categories from the granular toggles. */
  saveGranular: (categories: Partial<ConsentCategories>) => Promise<void>;
  /** Full withdrawal — records a new row with withdrawn_at. */
  withdraw: () => Promise<void>;
  /** Force the banner to re-appear (sets state back to null). */
  reopen: () => void;
}

/**
 * React hook for consent state management. Subscribes to localStorage updates
 * across tabs so if the user changes consent in one tab, other tabs react.
 */
export function useConsent(): UseConsentReturn {
  const [state, setState] = useState<ConsentState | null>(() => readConsentState());

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === CONSENT_STORAGE_KEY || e.key === LEGACY_TRACKING_CONSENT_KEY) {
        setState(readConsentState());
      }
    }
    function onSameWindowChange() {
      setState(readConsentState());
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener(CONSENT_CHANGE_EVENT, onSameWindowChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CONSENT_CHANGE_EVENT, onSameWindowChange);
    };
  }, []);

  const persist = useCallback((next: ConsentState) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
      // Keep legacy key in sync so any old reader still works
      localStorage.setItem(LEGACY_TRACKING_CONSENT_KEY, next.categories.analytics ? 'true' : 'false');
      setState(next);
      window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT));
    } catch (err) {
      logger.warn('[useConsent] Failed to persist consent state:', err);
    }
  }, []);

  const recordDecision = useCallback(
    async (categories: ConsentCategories) => {
      const next: ConsentState = {
        version: CONSENT_VERSION,
        categories,
        acceptedAt: new Date().toISOString(),
        withdrawnAt: null,
        sessionId: state?.sessionId || getOrCreateSessionId(),
      };
      persist(next);
      await postConsentToBackend('/api/consent', {
        categories,
        sessionId: next.sessionId,
        version: CONSENT_VERSION,
      });
    },
    [persist, state?.sessionId]
  );

  const acceptAll = useCallback(async () => {
    await recordDecision(ALL_GRANTED);
  }, [recordDecision]);

  const rejectAll = useCallback(async () => {
    await recordDecision(NECESSARY_ONLY);
  }, [recordDecision]);

  const saveGranular = useCallback(
    async (partial: Partial<ConsentCategories>) => {
      const merged: ConsentCategories = {
        necessary: true, // always true per Loi 25
        analytics: partial.analytics ?? false,
        marketing: partial.marketing ?? false,
      };
      await recordDecision(merged);
    },
    [recordDecision]
  );

  const withdraw = useCallback(async () => {
    const next: ConsentState = {
      version: CONSENT_VERSION,
      categories: NECESSARY_ONLY,
      acceptedAt: new Date().toISOString(),
      withdrawnAt: new Date().toISOString(),
      sessionId: state?.sessionId || getOrCreateSessionId(),
    };
    persist(next);
    await postConsentToBackend('/api/consent/withdraw', {
      sessionId: next.sessionId,
    });
  }, [persist, state?.sessionId]);

  const reopen = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      // The legacy key is also read as a completed decision. Keeping it would
      // immediately hide the banner again for users revising prior consent.
      localStorage.removeItem(LEGACY_TRACKING_CONSENT_KEY);
      setState(null);
      window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT));
    } catch (err) {
      logger.warn('[useConsent] Failed to reopen banner:', err);
    }
  }, []);

  const hasConsent = useCallback(
    (category: ConsentCategory) => {
      if (category === 'necessary') return true;
      if (!state) return false;
      if (state.withdrawnAt) return false;
      return !!state.categories[category];
    },
    [state]
  );

  return {
    state,
    isDecided: state !== null && !state.withdrawnAt,
    hasConsent,
    acceptAll,
    rejectAll,
    saveGranular,
    withdraw,
    reopen,
  };
}
